import { create } from "zustand";

export interface XY {
  x: number;
  y: number;
}

interface LayoutState {
  positions: Record<string, XY>;
  highlightedSystemId: string | null;
  pendingWrites: Set<string>;
  isDragging: Set<string>;

  setHighlightedSystemId: (id: string | null) => void;

  setPosition: (id: string, pos: XY) => void;
  setPositions: (updates: Record<string, XY>) => void;
  resetLayout: (positions: Record<string, XY>) => void;
  prune: (systemIds: Set<string>) => void;

  markPending: (id: string) => void;
  clearPending: (id: string) => void;

  markDragging: (id: string) => void;
  clearDragging: (id: string) => void;
}

export const useLayoutStore = create<LayoutState>((set) => ({
  positions: {},
  highlightedSystemId: null,
  pendingWrites: new Set(),
  isDragging: new Set(),

  setHighlightedSystemId: (id) =>
    set((s) => (s.highlightedSystemId === id ? s : { highlightedSystemId: id })),

  setPosition: (id, pos) =>
    set((s) => ({ positions: { ...s.positions, [id]: pos } })),
  setPositions: (updates) =>
    set((s) => ({ positions: { ...s.positions, ...updates } })),
  resetLayout: (positions) => set({ positions: { ...positions } }),
  prune: (systemIds) =>
    set((s) => {
      let positionsChanged = false;
      const nextPositions: Record<string, XY> = {};
      for (const [id, pos] of Object.entries(s.positions)) {
        if (systemIds.has(id)) nextPositions[id] = pos;
        else positionsChanged = true;
      }
      let nextHighlightedSystemId = s.highlightedSystemId;
      if (nextHighlightedSystemId && !systemIds.has(nextHighlightedSystemId)) {
        nextHighlightedSystemId = null;
      }
      let nextDragging = s.isDragging;
      let draggingCloned = false;
      for (const id of s.isDragging) {
        if (!systemIds.has(id)) {
          if (!draggingCloned) {
            nextDragging = new Set(s.isDragging);
            draggingCloned = true;
          }
          nextDragging.delete(id);
        }
      }
      if (
        !positionsChanged &&
        nextHighlightedSystemId === s.highlightedSystemId &&
        !draggingCloned
      )
        return s;
      return {
        positions: positionsChanged ? nextPositions : s.positions,
        highlightedSystemId: nextHighlightedSystemId,
        isDragging: nextDragging,
      };
    }),

  markPending: (id) =>
    set((s) => {
      if (s.pendingWrites.has(id)) return s;
      const next = new Set(s.pendingWrites);
      next.add(id);
      return { pendingWrites: next };
    }),
  clearPending: (id) =>
    set((s) => {
      if (!s.pendingWrites.has(id)) return s;
      const next = new Set(s.pendingWrites);
      next.delete(id);
      return { pendingWrites: next };
    }),

  markDragging: (id) =>
    set((s) => {
      if (s.isDragging.has(id)) return s;
      const next = new Set(s.isDragging);
      next.add(id);
      return { isDragging: next };
    }),
  clearDragging: (id) =>
    set((s) => {
      if (!s.isDragging.has(id)) return s;
      const next = new Set(s.isDragging);
      next.delete(id);
      return { isDragging: next };
    }),
}));
